import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { playerAPI } from '../services/api';

const Wrap = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: 1.5rem 1rem 3rem;
`;

const Back = styled(Link)`
  display: inline-block;
  margin-bottom: 1rem;
  color: #043663;
  font-weight: 600;
  text-decoration: none;
  font-size: 0.9rem;
`;

const CoverFrame = styled.div`
  border-radius: 14px;
  margin-bottom: 1.25rem;
  overflow: hidden;
  background: ${(p) =>
    p.$placeholder ? 'linear-gradient(135deg, #043663, #0ea5e9)' : '#0b1220'};
  min-height: ${(p) => (p.$placeholder ? '220px' : '0')};
`;

const CoverImg = styled.img`
  display: block;
  width: 100%;
  height: auto;
  object-fit: contain;
`;

const Title = styled.h1`
  margin: 0 0 0.5rem;
  font-size: 1.75rem;
  font-weight: 800;
  color: #0f172a;
`;

const Meta = styled.p`
  margin: 0 0 1rem;
  color: #64748b;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const Desc = styled.div`
  white-space: pre-wrap;
  color: #334155;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const Cta = styled.button`
  border: none;
  background: #043663;
  color: #fff;
  font-weight: 700;
  padding: 0.85rem 1.25rem;
  border-radius: 10px;
  cursor: pointer;
  font-size: 1rem;
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const StatusPill = styled.span`
  display: inline-block;
  margin-bottom: 1rem;
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  background: #ecfdf5;
  color: #065f46;
  font-size: 0.8rem;
  font-weight: 700;
`;

export default function PlayerOrganisedTournamentDetailPage() {
  const { t } = useTranslation();
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, refetch } = useQuery(
    ['playerOrganisedEvent', eventId],
    () => playerAPI.getOrganisedEvent(eventId),
    { enabled: !!eventId }
  );

  const payload = data?.data || data?.data?.data || {};
  const event = payload.event || payload;
  const myBooking = payload.myBooking || null;
  const slots = Array.isArray(event?.slots) ? event.slots : [];
  const mustPickSlot = slots.length > 1;
  const [slotId, setSlotId] = useState('');

  useEffect(() => {
    const booking = searchParams.get('booking');
    const sessionId = searchParams.get('session_id');
    if (booking === 'success' && sessionId && eventId) {
      playerAPI
        .verifyOrganisedEventCheckout(eventId, sessionId)
        .then(() => {
          toast.success(t('playerOrganisedEvents.bookingConfirmed', 'Booking confirmed!'));
          queryClient.invalidateQueries(['playerOrganisedBookings']);
          queryClient.invalidateQueries(['playerOrganisedTournaments']);
          refetch();
          navigate(`/player/events/tournaments/${eventId}`, { replace: true });
        })
        .catch((e) => {
          toast.error(e?.response?.data?.message || t('playerOrganisedEvents.verifyFailed', 'Could not verify payment'));
        });
    }
  }, [searchParams, eventId, navigate, queryClient, refetch, t]);

  const checkout = useMutation(
    () => {
      const chosen = mustPickSlot ? slotId : slots[0]?.id;
      return playerAPI.checkoutOrganisedEvent(eventId, chosen ? { slotId: chosen } : {});
    },
    {
    onSuccess: (res) => {
      const d = res?.data || res;
      if (d.free) {
        toast.success(t('playerOrganisedEvents.bookingConfirmed', 'Booking confirmed!'));
        queryClient.invalidateQueries(['playerOrganisedBookings']);
        queryClient.invalidateQueries(['playerOrganisedEvent', eventId]);
        queryClient.invalidateQueries(['playerOrganisedTournaments']);
        refetch();
        return;
      }
      if (d.checkoutUrl) {
        window.location.href = d.checkoutUrl;
        return;
      }
      toast.error(t('playerOrganisedEvents.checkoutFailed', 'Checkout failed'));
    },
    onError: (e) =>
      toast.error(e?.response?.data?.message || t('playerOrganisedEvents.checkoutFailed', 'Checkout failed')),
  });

  if (isLoading) {
    return (
      <Wrap>
        <p>{t('playerOrganisedEvents.loading', 'Loading…')}</p>
      </Wrap>
    );
  }

  if (!event?.id) {
    return (
      <Wrap>
        <Back to="/player/tournaments?area=tournaments">{t('playerOrganisedEvents.backToTournaments', '← Back to Tournaments')}</Back>
        <p>{t('playerOrganisedEvents.notFound', 'Tournament not found')}</p>
      </Wrap>
    );
  }

  const cover =
    event.coverImageUrl ||
    (event.hasCoverImage ? `/api/public/organised-events/${event.id}/cover-image` : null);
  const isFull = event.status === 'full' || event.remainingSpots <= 0;
  const alreadyBooked = myBooking?.status === 'confirmed';
  const bookedSlot = alreadyBooked
    ? slots.find((s) => String(s.id) === String(myBooking?.slotId))
    : null;
  const effectiveSlotId = mustPickSlot ? slotId : slots[0]?.id || '';
  const selectedSlot = slots.find((s) => String(s.id) === String(effectiveSlotId));
  const singleSlot = slots.length === 1 ? slots[0] : null;
  const canBook =
    event.status === 'published' &&
    !isFull &&
    (!myBooking || myBooking.status !== 'confirmed') &&
    (!mustPickSlot || (!!slotId && !selectedSlot?.isFull)) &&
    !(singleSlot?.isFull);

  return (
    <Wrap>
      <Back to="/player/tournaments?area=tournaments">{t('playerOrganisedEvents.backToTournaments', '← Back to Tournaments')}</Back>
      {cover ? (
        <CoverFrame>
          <CoverImg src={cover} alt={event.name || ''} />
        </CoverFrame>
      ) : (
        <CoverFrame $placeholder />
      )}
      <Title>{event.name}</Title>
      {alreadyBooked && (
        <StatusPill>{t('playerOrganisedEvents.youAreBooked', 'You are booked')}</StatusPill>
      )}
      <Meta>
        {event.clubName || t('playerOrganisedEvents.venueTbd', 'Venue TBC')}
        {event.clubAddress ? ` · ${event.clubAddress}` : ''}
        <br />
        {bookedSlot
          ? `${bookedSlot.label ? `${bookedSlot.label} · ` : ''}${new Date(bookedSlot.startsAt).toLocaleString()}${
              bookedSlot.endsAt ? ` → ${new Date(bookedSlot.endsAt).toLocaleString()}` : ''
            }`
          : mustPickSlot
            ? t('playerOrganisedEvents.chooseStartTime', 'Choose a start time below')
            : singleSlot
              ? `${singleSlot.label ? `${singleSlot.label} · ` : ''}${new Date(singleSlot.startsAt).toLocaleString()}${
                  singleSlot.endsAt ? ` → ${new Date(singleSlot.endsAt).toLocaleString()}` : ''
                }`
              : `${event.startsAt ? new Date(event.startsAt).toLocaleString() : '—'}${
                  event.endsAt ? ` → ${new Date(event.endsAt).toLocaleString()}` : ''
                }`}
        <br />
        {Number(event.priceChf) <= 0
          ? t('playerOrganisedEvents.free', 'Free')
          : `CHF ${Number(event.priceChf).toFixed(2)}`}
        {' · '}
        {isFull || event.status === 'full'
          ? t('playerOrganisedEvents.full', 'Full')
          : t('playerOrganisedEvents.spotsLeft', '{{count}} spots left', {
              count: event.remainingSpots,
            })}
      </Meta>
      {event.description && <Desc>{event.description}</Desc>}
      {mustPickSlot && !alreadyBooked && event.status === 'published' && (
        <div style={{ marginBottom: '1.25rem' }}>
          <Meta style={{ marginBottom: '0.5rem', fontWeight: 700, color: '#0f172a' }}>
            {t('playerOrganisedEvents.startTimes', 'Start times')}
          </Meta>
          {slots.map((s) => (
            <label
              key={s.id}
              style={{
                display: 'flex',
                gap: '0.65rem',
                alignItems: 'flex-start',
                padding: '0.7rem 0.8rem',
                marginBottom: '0.45rem',
                border: `1px solid ${String(slotId) === String(s.id) ? '#043663' : '#e2e8f0'}`,
                borderRadius: 10,
                background: s.isFull ? '#f8fafc' : '#fff',
                cursor: s.isFull ? 'not-allowed' : 'pointer',
              }}
            >
              <input
                type="radio"
                name="event-slot"
                value={s.id}
                disabled={s.isFull}
                checked={String(slotId) === String(s.id)}
                onChange={() => setSlotId(s.id)}
                style={{ marginTop: 3 }}
              />
              <span>
                <strong>{s.label || new Date(s.startsAt).toLocaleString()}</strong>
                {s.label ? (
                  <>
                    <br />
                    {new Date(s.startsAt).toLocaleString()}
                    {s.endsAt ? ` → ${new Date(s.endsAt).toLocaleString()}` : ''}
                  </>
                ) : s.endsAt ? (
                  <>
                    <br />
                    {`Ends ${new Date(s.endsAt).toLocaleString()}`}
                  </>
                ) : null}
                <br />
                <span style={{ color: '#64748b', fontSize: '0.85rem' }}>
                  {s.isFull
                    ? t('playerOrganisedEvents.full', 'Full')
                    : t('playerOrganisedEvents.spotsLeft', '{{count}} spots left', {
                        count: s.remainingSpots,
                      })}
                </span>
              </span>
            </label>
          ))}
        </div>
      )}
      {canBook && (
        <Cta
          type="button"
          disabled={checkout.isLoading || (mustPickSlot && !slotId)}
          onClick={() => {
            if (mustPickSlot && !slotId) {
              toast.error(t('playerOrganisedEvents.pickStartTime', 'Pick a start time first'));
              return;
            }
            checkout.mutate();
          }}
        >
          {checkout.isLoading
            ? t('playerOrganisedEvents.booking', 'Booking…')
            : t('playerOrganisedEvents.bookMySpot', 'Book my spot')}
        </Cta>
      )}
      {!canBook && !alreadyBooked && (isFull || event.status === 'full') && (
        <Meta>{t('playerOrganisedEvents.fullBlocked', 'This tournament is full.')}</Meta>
      )}
      {!canBook && !alreadyBooked && event.status === 'closed' && (
        <Meta>{t('playerOrganisedEvents.closed', 'Bookings are closed for this tournament.')}</Meta>
      )}
    </Wrap>
  );
}
